import {
  Avatar,
  AvatarBadge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { HiMenuAlt1 } from "react-icons/hi";

import { useAppConfig, useAuth } from "../../../context";
import React, { useEffect } from "react";
// import { questionBookSvgIcon } from "../../../utils";
import { useTranslation } from "react-i18next";
import { NotificationModel } from "../../Nofitication/NotificationModel";
import antaiaLogo from "../../../assets/img/antaia-logo.png";
import esg360Logo from "../../../assets/img/esg-teal-logo.png";

import { BsFillGearFill } from "react-icons/bs";
import { useRightSidebar } from "../../../context/RightSidebarContext";
import AIAssistPanel from "pages/ISSB/components/AIAssistPanel/AIAssistPanel";
import { useNavbar } from "./NavbarProvider";
import KnowledgeBase from "pages/KnowledgeBase";
import { BookOpenText, Sparkle } from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import { i18n } from "utils/text-utils";
import DevOnly from "context/Auth/DevOnly";
import { useQuery } from "react-query";
import { organizationService } from "api/organization-administration";
import { useDispatch } from "react-redux";
/**
 * Top navbar
 */
function Navbar() {
  const { navbarContent } = useNavbar();

  // control hamburger show/hide behaviour for mobile devices
  const { toggleSidebar } = useAppConfig();

  // default to "esg", either antaia or esg.
  const branding = process.env.REACT_APP_BRANDING || "esg";

  return (
    <Container id="navbar" minW="100vw" paddingBlock="4" px={8}>
      <Flex gap="2" justifyContent="space-between" alignItems="center">
        <IconButton /* Hm... This hamburger-for-mobile wasn't in the NavbarV2 code -- but surely we want it for mobile UI? */
          display={{ base: "inline-flex", md: "none" }}
          fontSize="lg"
          variant="outline"
          color="current"
          onClick={() => toggleSidebar()}
          icon={<HiMenuAlt1 />}
        />
        {/*<Image width="32" src={antaiaLogo} alt="company-logo" />*/}
        <Image width="32" src={branding === "esg" ? esg360Logo : antaiaLogo} alt="company-logo" />
        <Flex gap="2" inlineSize="full" justifyContent={"space-between"}>
          <Flex gap={4} mx={6}>
            <Box>{navbarContent /* e.g. (Select Round) dropdown */}</Box>
          </Flex>
          <Flex direction="row" gap={4}>
            <TopRightNav />
            <UserNav />
            {/* This is due to move to the sidebar -- but meanwhile lets not lose the functionality */}
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
}

/**
 * The ESG360° company name
 */
export const ESG360 = "ESG360°";

/**
 * Top right navigation menu
 * See https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=121-12580&t=hcRCq2SjYEdBbZO1-0
 */
function TopRightNav() {
  // const [rightSidebarLabel, setRightSidebarLabel] = useState();
  const { setRightSidebar, doOpen, isOpen, doClose, activationId } = useRightSidebar();

  // `label` used to recognise the right sidebar contents
  const toggleRightSidebar = (contents, label) => {
    setRightSidebar(contents);
    // setRightSidebarLabel(label);
    if (label === activationId && isOpen) {
      doClose();
    } else if (!isOpen) {
      doOpen(label);
    }
  };

  // To Close the right hand sidebar when ESC key is pressed on the keyboard
  useEffect(()=>{
    const handleKeyDown = (event) => {
      if(event.key === 'Escape') {
        doClose();
      }
    }

    // Adding the event listner to the window
    window.addEventListener('keydown', handleKeyDown);

    // Cleaning up the event listner on page unmount
    return () => window.removeEventListener('keydown', handleKeyDown);
  },[doClose])

  const t = i18n({ keyPrefix: "appHeader" });

  let showAI = true; // Use-case: If we need to disable the AI for some area of the app

  return (
    <Box id="top-right-nav" display="flex" alignItems="center">
      <Button
        variant="ghost"
        onClick={() => toggleRightSidebar(<KnowledgeBase />, "KB")}
        title={t("Knowledge Base")}
        fontSize="14px" // TODO: temp fix with fixed font size, chakra theme value is not working here
        rightIcon={<BookOpenText color={colors.navIconLime} />}
      >
        {t("Knowledge Base")}
      </Button>

      {showAI && (
        <Button
          variant="ghost"
          onClick={() => toggleRightSidebar(<AIAssistPanel />, "AI")}
          title={t("AI Assistant")}
          fontSize="14px" // TODO: temp fix with fixed font size, chakra theme value is not working here
          rightIcon={<Sparkle color={colors.navIconLime} />}
        >
          {t("AI Assistant")}
        </Button>
      )}

      <DevOnly>
        <IconButton
          display="none" // TODO: move settings here
          type="button"
          icon={<BsFillGearFill />}
          variant="ghost"
          onClick={(e) => alert("TODO: move settings here")}
          title={t("Settings")}
        />
      </DevOnly>

      <NotificationModel />
    </Box>
  );
}

/**
 * User navigation menu - who is logged in? + logout
 * This will move to the bottom of the left sidebar:
 * see https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=121-12580&t=hcRCq2SjYEdBbZO1-0
 */
function UserNav() {
  const { t } = useTranslation("translation", {
    keyPrefix: "appHeader",
  });
  const { logout, user } = useAuth();
  const dispatch = useDispatch();

  const handleLogout = () => {
    localStorage.clear();
    logout();
  };

  const { getAccessTokenSilently } = useAuth();

  const org_id = user?.org_id;
  const { data: orgData } = useQuery({
    queryKey: ["org", org_id],
    queryFn: () => getAccessTokenSilently().then(() => organizationService.get(org_id)),
    enabled: !!org_id,
  });


  return (
    <Menu>
      <MenuButton
        fontSize="14px" // temp fix with fixed font size, chakra theme value is not working here
        as={Button}
        variant="ghost"
      >
        <HStack>
          <Avatar size="xs" name={user.name}>
            <AvatarBadge boxSize="1em" bg="green.500" />
          </Avatar>
          {/*<Text display={{ base: "none", lg: "block" }}>{user.name}</Text>*/}
        </HStack>
      </MenuButton>
      <MenuList>
        <MenuGroup title={user.name}>
          <MenuItem fontSize="14px" title={org_id}>
            {orgData?.name || org_id}
          </MenuItem>
          <MenuItem fontSize="14px" onClick={() => handleLogout()}>
            Logout
          </MenuItem>
        </MenuGroup>
      </MenuList>
    </Menu>
  );
}

export default Navbar;
